import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import cs from 'classnames';

import DownloadFilesButton from 'features/media/components/molecules/DownloadFilesButton';
import FileSelectAllButton from 'features/media/components/molecules/FileSelectAllButton';
import { useOrganizations } from 'features/organizations/useOrganizations';
import { useReviewModules } from 'features/reviewModules/useReviewModules';
import ModuleHeaderIcons from 'features/submissions/components/molecules/ModuleHeaderIcons';
import RevisionsList from 'features/submissions/components/molecules/RevisionsList';
import SubmissionModuleStatusSelect from 'features/submissions/components/molecules/SubmissionModuleStatusSelect';
import ModuleAssets from 'features/submissions/components/organisms/ModuleAssets';
import { useSubmissions } from 'features/submissions/useSubmissions';
import CenteredSpinner from 'features/ui/CenteredSpinner';
import { useRightDrawer } from 'features/ui/useRightDrawer';
import { ProfilePicture } from 'features/users/components/molecules/ProfilePicture';
import { Box, Flex, Text } from 'shared/components/display';
import Divider from 'shared/components/molecules/Divider';
import ToggleGalleryListView from 'shared/components/molecules/ToggleGalleryListView';
import ModuleReview from 'shared/components/organisms/ModuleReview';

import { COLLECTION_COPY } from 'shared/config/copy';
import { PERMISSIONS_MAP } from 'shared/config/permissions';
import { usePermissions } from 'shared/hooks/usePermissions';
import { intl } from 'shared/lib/intl';
import { TEXT_VARIANTS } from 'shared/styles/text';
import { BrkfstFile } from 'shared/typings/file';
import { Revision, RevisionStatus } from 'shared/typings/revisions';
import { ModuleSubmissionStatus, Submission, SubmissionStatus } from 'shared/typings/submissions';
import { UserType } from 'shared/typings/user/enums';
import { LinkCreator } from 'shared/utilities/linkUtility';

import styles from './styles';

const MODULE_COLLECTION_NAME = 'Module Files';

interface Props {
  submission: Submission;
  isCreator?: boolean;
}

const SubmissionModules: React.FC<Props> = ({ submission, isCreator }) => {
  const { isAuthorized } = usePermissions();
  const [selectedFiles, setSelectedFiles] = useState<BrkfstFile[]>([]);
  const { loading } = useSubmissions();
  const { organizations } = useOrganizations();
  const { setRightDrawer } = useRightDrawer(true);

  const [moduleLoading, setModuleLoading] = useState<number | null>(null);
  const {
    brief: {
      id: briefId,
      organizationId,
      accountId,
      name: briefName,
      account: { name: brandName },
    },
    creatorId,
    moduleSubmissions,
  } = submission;
  const downloadInfo = useMemo(
    () => ({
      brandName,
      briefName,
      collectionName: MODULE_COLLECTION_NAME,
      orgName: organizations.find(({ id }) => id === organizationId)?.name || brandName,
    }),
    [brandName, briefName, organizations, organizationId],
  );
  const { totalFiles, files } = useMemo(() => {
    const files = moduleSubmissions.reduce((acc: BrkfstFile[], mod) => {
      return [...acc, ...mod.files];
    }, []);
    return {
      totalFiles: files.length,
      files,
    };
  }, [moduleSubmissions]);

  const isDraft = submission.status === SubmissionStatus.DRAFT;

  const canUpdateModuleState = isAuthorized(organizationId, PERMISSIONS_MAP.UPDATE_MODULE_STATE.API);

  const onOpenFeedbackDrawer = (
    revisions: Revision[] | undefined,
    moduleNum: number,
    isCreator = false,
    status: ModuleSubmissionStatus,
  ) => {
    if (revisions?.length) {
      // if module is still in review, only show the creator revisions that are done
      const filteredRevisions =
        isCreator && status === ModuleSubmissionStatus.REVIEW
          ? revisions.filter((rev) => rev.status === RevisionStatus.DONE)
          : revisions;
      return setRightDrawer({
        key: moduleNum,
        Content: <RevisionsList revisions={filteredRevisions} brandName={brandName} />,
        headerText: intl.formatMessage(
          {
            id: 'DRAWER_TITLE_REVISIONS',
          },
          {
            briefName,
            moduleNum,
          },
        ),
      });
    }
  };

  if (loading) {
    return <CenteredSpinner />;
  }

  if (moduleSubmissions.length === 0) {
    return <></>;
  }

  const reviewLink = LinkCreator.createLink({
    userType: UserType.ORG,
    routeKey: 'VIEW_INDIVIDUAL_BRIEF',
    variables: {
      organizationId: organizationId,
      accountId: accountId,
      briefId: briefId,
    },
    query: {
      activeTab: 3,
      openLightbox: true,
      sId: submission.id,
    },
  });

  const profileLink = LinkCreator.createLink({
    userType: UserType.ORG,
    routeKey: 'USER_PROFILE',
    variables: {
      userId: submission.creator.id,
    },
  });

  const showModuleReview = isAuthorized(organizationId, PERMISSIONS_MAP.REVIEW_MODULES.API);

  const onSelectAllFilesClick = () => {
    if (selectedFiles.length === totalFiles) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files);
    }
  };

  return (
    <Box className="submission-modules" css={styles}>
      <Flex className="submission-modules__name-review-wrapper">
        {!isCreator && (
          <Flex className="submission-modules__creator-info">
            <ProfilePicture customProfileLink={profileLink} openNewTab user={submission.creator} size="xs" />
            <Link target={'_blank'} to={profileLink} data-cy="submission-modules__creator-name">
              <Text className="submission-modules__creator-name">{submission.creator.fullName}</Text>
            </Link>
          </Flex>
        )}
        {showModuleReview && (
          <ModuleReview
            alignItems="center"
            organizationId={organizationId}
            accountId={accountId}
            briefId={briefId}
            briefName={briefName}
            brandName={brandName}
            submissionId={submission.id}
            reviewLink={reviewLink}
          />
        )}
      </Flex>

      <Flex
        className={cs('submission-modules__control-wrapper', {
          'submission-modules__control-wrapper--no-selection': isCreator,
        })}
      >
        {!isCreator && (
          <Flex className="submission-modules__select-actions">
            <FileSelectAllButton
              onSelectAllFilesClick={onSelectAllFilesClick}
              selectedFiles={selectedFiles}
              totalFiles={totalFiles}
            />
            {Boolean(selectedFiles.length) && (
              <DownloadFilesButton
                splitButtonVariant="secondary"
                selectedFiles={selectedFiles}
                downloadInfo={downloadInfo}
                tooltip={COLLECTION_COPY.TOOLTIP_DOWNLOAD_SELECTED_FILES}
                promptTitle={COLLECTION_COPY.MODAL_DOWNLOAD_FILES}
              />
            )}
          </Flex>
        )}
        <ToggleGalleryListView />
      </Flex>
      {moduleSubmissions.map((submissionModule) => {
        return (
          <Box key={submissionModule.moduleNum} dataCy={`submission-modules__mod-${submissionModule.moduleNum}`}>
            <Divider
              heading={submissionModule.module ? submissionModule?.module.name : `Module ${submissionModule.moduleNum}`}
              Component={
                isDraft ? undefined : (
                  <Flex className="submission-modules__header">
                    <ModuleHeaderIcons
                      status={submissionModule.status}
                      isModuleLoading={moduleLoading === submissionModule.id}
                      revisions={submissionModule.revisions}
                      onClick={() =>
                        onOpenFeedbackDrawer(
                          submissionModule.revisions,
                          submissionModule.moduleNum,
                          isCreator,
                          submissionModule.status,
                        )
                      }
                      moduleNum={submissionModule.moduleNum}
                      isCreator={isCreator}
                    />
                    {canUpdateModuleState && (
                      <SubmissionModuleStatusSelect
                        className="submission-modules__status-select"
                        creatorId={creatorId}
                        setModuleLoading={setModuleLoading}
                        submissionModule={submissionModule}
                        briefId={briefId}
                      />
                    )}
                  </Flex>
                )
              }
              variant={TEXT_VARIANTS.H6}
            />
            <ModuleAssets
              assets={submissionModule.files}
              status={submissionModule.status}
              organizationId={organizationId}
              setSelectedFiles={setSelectedFiles}
              selectedFiles={selectedFiles}
              moduleNum={submissionModule.moduleNum}
              accountId={accountId}
              moduleId={submissionModule.id}
              briefName={briefName}
              brandName={brandName}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default SubmissionModules;
